import { Tooltip } from 'antd';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiClassificationStatusEnum } from '../../../enums/AiClassificationStatusEnum';
import { FileOfflineStatusEnum } from '../../../enums/FileOfflineStatusEnum';
import { FileSalesStatusEnum } from '../../../enums/FileSalesStatusEnum';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { useAuth } from '../../../hooks/useAuth';
import { useFilePassword } from '../../../hooks/useFilePassword';
import type File from '../../../interfaces/File';
import type { FileOffline } from '../../../interfaces/FileApiService';
import { useAppStore } from '../../../store/store';
import { formatShareExpirationAt } from '../../../utils/fileExpirationUtils';
import {
  formatRemainingTimeFromSeconds,
  getFileTitle,
  getFolderHex,
  isImageFileExtension,
  removeInternalFileExtension,
  removeShortcutExtension,
  ticksToMs,
} from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';
import type { iconComponents } from '../../Core/IconImports';
import FileAiClassificationStatusIcon from '../FileAiClassificationStatusIcon';
import FileAntivirusStatusIcon from '../FileAntivirusStatusIcon';
import type { FilesTableActionsCellProps } from '../FilesTableActionsCell/FilesTableActionsCell';
import FilesTableActionsCell from '../FilesTableActionsCell';
import { getShareExpirationIconState } from './getShareExpirationIconState';

type FilesTableTitleCellInlineActions = Omit<FilesTableActionsCellProps, 'file' | 'variant'>;

interface FilesTableTitleCellProps {
  file: File;
  filesOffline: FileOffline[];
  inlineActions?: FilesTableTitleCellInlineActions;
}

/**
 * Renders the title cell for a file in the files table, including icon, name,
 * status indicators, and thumbnail image preview when supported.
 */
const FilesTableTitleCell: React.FC<FilesTableTitleCellProps> = ({
  file,
  filesOffline,
  inlineActions,
}) => {
  const { isInFileRoute } = useAuth();
  const { t } = useTranslation();
  const { hasPassword } = useFilePassword();

  // App store selectors for folder password session state.
  const filePasswordSessions = useAppStore((s) => s.filePasswordSessions);
  const getFilePasswordSessionInfo = useAppStore((s) => s.getFilePasswordSessionInfo);
  const removeFilePasswordSession = useAppStore((s) => s.removeFilePasswordSession);

  /**
   * Find the current password session for this file (folder) if it exists.
   * Only applies when the file is a folder and has an active session.
   */
  const currentFilePasswordSession = useMemo(() => {
    if (!file?.isFolder || !file?.id) return null;
    return getFilePasswordSessionInfo(file.id);
  }, [file?.id, file?.isFolder, filePasswordSessions, getFilePasswordSessionInfo]);

  /**
   * State to store how many seconds are left in the password session for this file.
   * Only maintained when there is an active session.
   */
  const [filePasswordRemainingSeconds, setFilePasswordRemainingSeconds] = useState<number | null>(
    null,
  );

  /**
   * Effect hook to update (and countdown) the remaining time for the file password session.
   * When the session expires, it removes itself.
   */
  useEffect(() => {
    if (!currentFilePasswordSession || !file?.id) {
      setFilePasswordRemainingSeconds(null);
      return;
    }

    // Updates the remaining time, and expires/removes the session if time runs out
    const updateRemainingTime = () => {
      const now = Date.now();
      const expiresAtMs = ticksToMs(currentFilePasswordSession.expiresAt);
      const remainingMs = expiresAtMs - now;
      const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));

      if (remainingSec <= 0) {
        removeFilePasswordSession(file.id);
        setFilePasswordRemainingSeconds(null);
        return;
      }

      setFilePasswordRemainingSeconds(remainingSec);
    };

    updateRemainingTime();
    const intervalId = window.setInterval(updateRemainingTime, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentFilePasswordSession, file?.id, removeFilePasswordSession]);

  /**
   * Computes the current offline status for this file, if it is being
   * handled via offline functionality (e.g., downloading, pending, etc).
   * Includes the corresponding icon for each state.
   */
  const currentFileOfflineStatus = useMemo(() => {
    const currentStatus = filesOffline?.find((offlineFile) => offlineFile.id === file.id)?.status;
    if (currentStatus) {
      switch (currentStatus) {
        case FileOfflineStatusEnum.Pending:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'ClockArrowDownIcon' as keyof typeof iconComponents,
          };
        case FileOfflineStatusEnum.Downloading:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'HardDriveDownloadIcon' as keyof typeof iconComponents,
          };
        case FileOfflineStatusEnum.Downloaded:
          return {
            title: t(`fileOfflineStatus.${currentStatus}`),
            icon: 'HardDriveIcon' as keyof typeof iconComponents,
          };
      }
    }

    return null;
  }, [filesOffline, file.id, t]);

  const fullName = removeInternalFileExtension(removeShortcutExtension(getFileTitle(file ?? {})));
  const dotIndex = !file.isFolder ? fullName.lastIndexOf('.') : -1;
  const baseName = dotIndex > 0 ? fullName.slice(0, dotIndex) : fullName;
  const ext = dotIndex > 0 ? fullName.slice(dotIndex) : '';

  const shareIconState = getShareExpirationIconState(file?.shareExpirationAt, Date.now());
  const shareExpirationDate =
    file?.shareExpirationAt != null && file.shareExpirationAt > 0
      ? formatShareExpirationAt(file.shareExpirationAt)
      : '';
  const shareTooltip =
    shareIconState.tooltipMode === 'shareExpiredOn'
      ? t('common.shareExpiredOn', { date: shareExpirationDate })
      : shareIconState.tooltipMode === 'expiresOn'
        ? t('common.expiresOn', { date: shareExpirationDate })
        : t('common.shared');

  return (
    <div className="files-table-title-cell flex min-h-0 w-full max-w-full min-w-0 flex-1 cursor-pointer flex-row items-center gap-2 py-0.5 hover:underline">
      <Tooltip title={removeInternalFileExtension(file.fileName)} placement="top">
        <FileIcon
          fileId={file?.id}
          isFolderPasswordProtected={file?.isPasswordSet}
          isFolderPasswordBypassed={file?.isPasswordBypass}
          isShortcut={file?.isShortcut}
          isFolder={file?.isFolder}
          fileExtension={removeShortcutExtension(file?.fileExtension ?? '')}
          secretPreset={file?.secretPreset}
          enableImagePreview={
            !file.isFolder &&
            isImageFileExtension(removeShortcutExtension(file?.fileExtension ?? ''))
          }
          className={file?.isFolder ? 'h-9! w-9!' : 'h-8! w-8!'}
          imageClassName="h-7! w-7!"
          style={{
            color: getFolderHex(file),
          }}
        />
      </Tooltip>
      {/* Group: title (content width, truncates when needed) + status icons right next to text; group does not grow so icons stay by the text */}
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <div
          className={clsx(
            'filename-click-handler flex min-w-0 items-baseline overflow-hidden leading-5',
            file?.isUnRead && 'font-bold',
          )}
        >
          <TruncatedTooltip title={fullName} className="min-w-0">
            {baseName}
          </TruncatedTooltip>
          {ext && <span className="shrink-0 whitespace-nowrap">{ext}</span>}
        </div>
        <div className="flex shrink-0 flex-row items-center gap-2">
          {/* Show countdown for active password session on folder (if present) */}
          {currentFilePasswordSession != null &&
            filePasswordRemainingSeconds !== null &&
            filePasswordRemainingSeconds > 0 && (
              <Tooltip
                title={
                  t('common.sessionExpiresIn', {
                    time: formatRemainingTimeFromSeconds(filePasswordRemainingSeconds),
                  }) ||
                  `Session expires in ${formatRemainingTimeFromSeconds(filePasswordRemainingSeconds)}`
                }
              >
                <div className="flex items-center justify-center gap-1">
                  <Icon name="LockOpenIcon" className="text-blue-dark h-5 w-5" />
                  <span className="font-mono text-sm font-medium">
                    {formatRemainingTimeFromSeconds(filePasswordRemainingSeconds)}
                  </span>
                </div>
              </Tooltip>
            )}
          {/* Show offline status (pending/downloading/downloaded) if has status */}
          {currentFileOfflineStatus && (
            <Tooltip title={currentFileOfflineStatus.title}>
              <div>
                <Icon name={currentFileOfflineStatus.icon} className="text-blue-dark h-5 w-5" />
              </div>
            </Tooltip>
          )}
          {/* Show favorite star if file is marked favorite */}
          {file?.isFavourite && (
            <Tooltip title={t('common.favourite')}>
              <div>
                <Icon name="StarFullIcon" className="text-blue-dark h-5 w-5" />
              </div>
            </Tooltip>
          )}

          {/* Show public share icon if file is public share */}
          {file?.isPublicShare && (
            <Tooltip title={t('common.publicLinkLabel')}>
              <div>
                <Icon name="LinkIcon" className="text-blue-dark h-5 w-5" />
              </div>
            </Tooltip>
          )}

          {/* Show sharing icon if file is shared */}
          {file?.isShared && (
            <Tooltip title={shareTooltip}>
              <div>
                <Icon name="Share2Icon" className={`${shareIconState.colorClass} h-5 w-5`} />
              </div>
            </Tooltip>
          )}
          {/* AI classification status icon */}
          {file?.aiOperationStatus !== AiClassificationStatusEnum.None && (
            <FileAiClassificationStatusIcon file={file} />
          )}
          <FileAntivirusStatusIcon file={file} />
          {/* Show sale/store icon if file is for sale */}
          {file?.salesStatus === FileSalesStatusEnum.ForSale && (
            <Tooltip title={t('common.forSale')}>
              <div>
                <Icon name="StoreIcon" className="text-blue-dark h-5 w-5" />
              </div>
            </Tooltip>
          )}
          {/* Show view/edit icon if viewing file in "Received" route (readonly or edit) */}
          {isInFileRoute(FilesTableEnum.Received) && (
            <Tooltip title={file?.isReadOnly ? t('common.viewer') : t('common.editor')}>
              <div>
                {file?.isReadOnly ? (
                  <Icon name="ViewIcon" className="text-yellow-dark h-5 w-5" />
                ) : (
                  <Icon name="SquarePenIcon" className="text-green-dark h-5 w-5" />
                )}
              </div>
            </Tooltip>
          )}
          {/* Show folder-lock icon if folder is password protected */}
          {file?.isFolder && hasPassword(file.id) && (
            <Tooltip title={t('common.protectedFolder')}>
              <div>
                <Icon name="FolderKeyIcon" className="text-blue-dark h-5 w-5" />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {inlineActions != null && (
        <FilesTableActionsCell file={file} variant="title" {...inlineActions} />
      )}
    </div>
  );
};

export default FilesTableTitleCell;
